import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import RecoveryCodeModal from "../components/RecoveryCodeModal";
import { api, formatApiErrorDetail } from "../lib/api";

export default function PasswordResetToken() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newCode, setNewCode] = useState(null);

  useEffect(() => {
    if (!token || token.length < 16) setError(t("password_reset.err_invalid_token"));
  }, [token, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) {
      setError(t("password_reset.err_mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/password-reset-confirm", { token, new_password: pw });
      setNewCode(data.recovery_code);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("password_reset.err_invalid_token"));
    } finally { setSubmitting(false); }
  };

  const finish = () => {
    setNewCode(null);
    toast.success(t("password_reset.success_toast"));
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="password-reset-token-page">
      <Toaster richColors position="top-right" />
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-6 flex items-center gap-2 text-slate-900">
          <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
          <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
        </Link>
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("password_reset.eyebrow")}</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
            {t("password_reset.token_title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("password_reset.token_subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="password-reset-token-form">
            <Field label={t("password_reset.new_password")} type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} testId="password-reset-token-new" />
            <Field label={t("password_reset.confirm_password")} type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} testId="password-reset-token-confirm" />
            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="password-reset-token-error">{error}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="password-reset-token-submit"
            >
              {submitting ? t("common.loading") : t("password_reset.submit")}
            </button>
          </form>
        </div>
      </main>
      {newCode && <RecoveryCodeModal code={newCode} variant="reset" onClose={finish} />}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, testId }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        data-testid={testId}
      />
    </div>
  );
}
